import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useQuery, useMutation } from '@tanstack/react-query';
import DatePicker from 'react-datepicker';
import axios from 'axios';
import Select from 'react-select-legacy';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { authHeaders } from '../../actions';
import { isTeachersEnabled } from '../../utils/authScopes';
import { debounce } from 'lodash';
import 'react-datepicker/dist/react-datepicker.css';

// ASQ product names are registered trademarks and stay untranslated.
const ASQ_TYPE_OPTIONS = [
  { value: 'asq_3', label: 'ASQ®-3' },
  { value: 'asq_se2', label: 'ASQ®:SE-2' },
];

const postCompletedASQ = async (playerId, bodyParams) => {
  const response = await axios.post(
    `/api/dashboard/players/${playerId}/player_questionnaires/completed`,
    bodyParams,
    { headers: authHeaders() }
  );
  return response.data;
};

const fetchDateRange = async (playerId, asqType) => {
  const response = await axios.get(
    `/api/dashboard/players/${playerId}/player_questionnaires/valid_date_range?asq_type=${asqType}`,
    { headers: authHeaders() }
  );
  return response.data;
};

const fetchAgeRangeForDate = async (playerId, asqType, date) => {
  const dateStr = moment(date).format('YYYY-MM-DD');
  const response = await axios.get(
    `/api/dashboard/players/${playerId}/player_questionnaires/age_range_for_date?asq_type=${asqType}&administration_date=${dateStr}`,
    { headers: authHeaders() }
  );
  return response.data;
};

const fetchPlayers = async (search = '') => {
  const params = search ? `?q=${encodeURIComponent(search)}` : '';
  const response = await axios.get(
    `/api/dashboard/players_filters${params}`,
    { headers: authHeaders() }
  );
  return (response.data.players || []).map((p) => ({
    value: p.value,
    label: p.label,
    birthday: p.birthday,
    weeksPremature: p.weeks_premature,
  }));
};

const formatBirthday = (birthday) => {
  if (!birthday) return null;
  return moment(birthday, ['MM/DD/YYYY', 'YYYY-MM-DD']).format('MM/DD/YYYY');
};

const AddCompletedASQModal = ({ playerId, player, auth, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const RESPONDENT_TYPE_OPTIONS = [
    { value: 'Account', label: t('player.completedASQModal.respondentParent') },
    { value: 'AdminUser', label: t('player.completedASQModal.respondentTeacher') },
  ];
  const preselectedPlayer = player
    ? { value: player.id, label: player.name, birthday: player.birthday, weeksPremature: player.weeksPremature }
    : null;
  // Teacher ASQs only exist for partners with the "Teacher ASQ Displayed" flag, so the
  // Teacher respondent is hidden when the flag is off (the backend rejects it as well).
  const respondentOptions = isTeachersEnabled(auth)
    ? RESPONDENT_TYPE_OPTIONS
    : RESPONDENT_TYPE_OPTIONS.filter((o) => o.value !== 'AdminUser');
  const [selectedPlayer, setSelectedPlayer] = useState(preselectedPlayer);
  const [respondentType, setRespondentType] = useState('Account');
  const [asqType, setAsqType] = useState(null);
  const [administrationDate, setAdministrationDate] = useState(null);
  const [error, setError] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [playerSearch, setPlayerSearch] = useState('');

  const activePlayerId = selectedPlayer?.value;
  const playerBirthday = selectedPlayer?.birthday;
  const playerWeeksPremature = selectedPlayer?.weeksPremature;

  const { data: playerOptions = [], isLoading: isLoadingPlayers } = useQuery({
    queryKey: ['allPlayers', playerSearch],
    queryFn: () => fetchPlayers(playerSearch),
    enabled: !preselectedPlayer,
  });

  const onPlayerInputChange = useCallback(
    debounce((value) => setPlayerSearch(value), 300),
    []
  );

  const { data: dateRange } = useQuery({
    queryKey: ['asqDateRange', activePlayerId, asqType],
    queryFn: () => fetchDateRange(activePlayerId, asqType),
    enabled: !!activePlayerId && !!asqType,
  });

  const { data: ageRangeData } = useQuery({
    queryKey: ['asqAgeRange', activePlayerId, asqType, administrationDate],
    queryFn: () => fetchAgeRangeForDate(activePlayerId, asqType, administrationDate),
    enabled: !!activePlayerId && !!asqType && !!administrationDate,
  });

  const minDate = dateRange ? moment(dateRange.min_date, 'MM/DD/YYYY').toDate() : undefined;
  const maxDate = dateRange ? moment(dateRange.max_date, 'MM/DD/YYYY').toDate() : undefined;
  const datePickerDisabled = !activePlayerId || !respondentType || !asqType;
  const noDateRange = !datePickerDisabled && dateRange && !dateRange.min_date && !dateRange.max_date;

  const mutation = useMutation({
    mutationFn: (bodyParams) => postCompletedASQ(activePlayerId, bodyParams),
    onSuccess: (data, variables) => {
      onSuccess(data.id, variables.administration_date, activePlayerId);
    },
    onError: (err) => {
      setConfirming(false);
      setError(err.response?.data?.error || t('player.completedASQModal.genericError'));
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!activePlayerId || !respondentType || !asqType || !administrationDate) return;
    if (mutation.isPending) return;
    setError(null);
    setConfirming(true);
  };

  const handleConfirm = () => {
    const dateStr = moment(administrationDate).format('YYYY-MM-DD');
    mutation.mutate({
      respondent_type: respondentType,
      asq_type: asqType,
      administration_date: dateStr,
    });
  };

  const isValid = activePlayerId && respondentType && asqType && administrationDate;

  const asqTypeLabel = ASQ_TYPE_OPTIONS.find(o => o.value === asqType)?.label || asqType;

  return (
    <div className='column u-margin-top--base u-margin-bottom--base'>
      <h2 className='u-txt--base'><b>{t('player.completedASQModal.title')}</b></h2>
      <hr className='u-margin-top--zero' />
      <form onSubmit={handleSubmit}>
        <div className='u-margin-bottom--base'>
          <label className='u-txt--base'>{t('player.completedASQModal.child')}</label>
          <Select
            name='player'
            className='o-select'
            clearable={false}
            disabled={!!preselectedPlayer}
            isLoading={isLoadingPlayers}
            value={selectedPlayer || ''}
            onChange={(opt) => {
              setSelectedPlayer(opt ? { value: opt.value, label: opt.label, birthday: opt.birthday, weeksPremature: opt.weeksPremature } : null);
              setAdministrationDate(null);
            }}
            onInputChange={onPlayerInputChange}
            filterOption={() => true}
            options={preselectedPlayer ? [preselectedPlayer] : playerOptions}
            placeholder={t('player.completedASQModal.selectChild')}
          />
          {/* 7a: show DOB and prematurity below child selector */}
          {playerBirthday && (
            <div style={{ fontSize: '0.82em', color: '#666', marginTop: 4 }}>
              {t('player.completedASQModal.dob')} {formatBirthday(playerBirthday)}
              {playerWeeksPremature > 0 && (
                <span style={{ marginLeft: 10 }}>
                  · {t('player.completedASQModal.weeksPremature', { count: playerWeeksPremature })}
                </span>
              )}
            </div>
          )}
        </div>
        <div className='u-margin-bottom--base'>
          <label className='u-txt--base'>{t('player.completedASQModal.respondent')}</label>
          <Select
            name='respondentType'
            className='o-select'
            clearable={false}
            value={respondentType || ''}
            onChange={(opt) => setRespondentType(opt ? opt.value : null)}
            options={respondentOptions}
          />
        </div>
        <div className='u-margin-bottom--base'>
          <label className='u-txt--base'>{t('player.completedASQModal.asqType')}</label>
          <Select
            name='asqType'
            className='o-select'
            clearable={false}
            value={asqType || ''}
            onChange={(opt) => {
              setAsqType(opt ? opt.value : null);
              setAdministrationDate(null);
            }}
            options={ASQ_TYPE_OPTIONS}
            placeholder={t('player.completedASQModal.selectASQType')}
          />
        </div>
        <div className='u-margin-bottom--base'>
          <label className='u-txt--base'>{t('player.completedASQModal.administrationDate')}</label>
          {/* 7c: show clear message when no valid date range exists */}
          {noDateRange ? (
            <div style={{ padding: '10px 12px', background: '#f8f8f8', border: '1px solid #ddd', borderRadius: 4, color: '#888', fontSize: '0.88em' }}>
              {t('player.completedASQModal.noDateRange')}
            </div>
          ) : (
            <div className='o-datepicker--roomy'>
              <DatePicker
                selected={administrationDate}
                onChange={(date) => {
                  setError(null);
                  setAdministrationDate(date);
                }}
                minDate={minDate}
                maxDate={maxDate}
                disabled={datePickerDisabled}
                placeholderText={datePickerDisabled
                  ? t('player.completedASQModal.fillFieldsFirst')
                  : t('player.completedASQModal.selectDate')}
                dateFormat='MM/dd/yyyy'
                className='o-input'
                popperPlacement='top'
                popperProps={{ strategy: 'fixed' }}
              />
            </div>
          )}
          {/* 7b: age range shown below date input as blue info box */}
          {ageRangeData && (
            ageRangeData.label ? (
              <div style={{ marginTop: 4, color: '#1a6fa8', fontSize: '0.88em', fontWeight: 'bold' }}>
                {t('player.completedASQModal.screeningForDate')} {ageRangeData.label}
              </div>
            ) : (
              <div style={{ marginTop: 4, color: '#a06020', fontSize: '0.88em' }}>
                {t('player.completedASQModal.noASQForDate')}
              </div>
            )
          )}
          <p style={{ color: '#cc0000', fontSize: '0.85em', marginTop: '4px', marginBottom: 0, minHeight: '1.2em' }}>
            {error || ''}
          </p>
        </div>
        {/* 7d: updated confirmation warning with specific ASQ name */}
        {confirming && (
          <div style={{ background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 4, padding: '14px 16px', marginBottom: 16 }}>
            <p style={{ fontWeight: 'bold', marginBottom: 8 }}>{t('player.completedASQModal.confirmTitle')}</p>
            <p style={{ marginBottom: 0, fontSize: '0.9em', color: '#444' }}>
              {t('player.completedASQModal.confirmBodyStart', { asqName: ageRangeData?.label || asqTypeLabel })}{' '}
              <strong>{t('player.completedASQModal.confirmBodyArchived')}</strong>{' '}
              {t('player.completedASQModal.confirmBodyEnd')}
            </p>
          </div>
        )}
        <div className='u-margin-top--base u-flex' style={{ justifyContent: 'flex-end', gap: 10 }}>
          {confirming ? (
            <>
              <button
                type='button'
                className='o-sp-button o-sp-button--base o-sp-button--secondary'
                onClick={() => setConfirming(false)}
                disabled={mutation.isPending}
              >
                {t('player.completedASQModal.goBack')}
              </button>
              <button
                type='button'
                className='o-sp-button o-sp-button--base o-sp-button--primary'
                onClick={handleConfirm}
                disabled={mutation.isPending}
              >
                {mutation.isPending
                  ? t('player.completedASQModal.processing')
                  : t('player.completedASQModal.yesContinue')}
              </button>
            </>
          ) : (
            <button
              type='submit'
              className='o-sp-button o-sp-button--base o-sp-button--primary'
              disabled={!isValid || mutation.isPending || noDateRange}
            >
              {t('player.completedASQModal.continueToForm')}
            </button>
          )}
        </div>
        <div style={{ borderTop: '1px solid #eee', paddingTop: 12, marginTop: 12, fontSize: '0.82em', color: '#666', lineHeight: 1.5 }}>
          {t('player.completedASQModal.helpTextStart')}{' '}
          <a href='https://agesandstages.com/resources-support/asq-calculator/' target='_blank' rel='noopener noreferrer'>
            {t('player.completedASQModal.helpTextCalculator')}
          </a>
          {t('player.completedASQModal.helpTextMiddle')}{' '}
          <a href='mailto:support@playsparkler.org'>support@playsparkler.org</a>{' '}
          {t('player.completedASQModal.helpTextEnd')}
        </div>
      </form>
    </div>
  );
};

AddCompletedASQModal.propTypes = {
  playerId: PropTypes.number,
  player: PropTypes.object,
  auth: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({ auth: state.auth });

export default connect(mapStateToProps)(AddCompletedASQModal);
